import React, { useEffect, useState } from "react";
import { masterAPI } from "../../../Masters/dataConfig";
import { axiosPrivate } from "../../../../axios/axios";
import { Autocomplete, Box, Button, CircularProgress, Dialog, Divider, Grid, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { color } from "../../../../StyleData/Config";
import { DeleteOutline, Domain, EditOutlined } from "@mui/icons-material";
import NormalTable from "../../../../Component/NormalTable";
import CreatePart from "./CreatePart";
import { toast } from "react-toastify";

const Partlist = ({ workOrderId, enableDisable }) => {
  const [partList, setPartList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [partDialogue, setPartDialogue] = useState(false);

  // Inline edit state
  const [editPartId, setEditPartId] = useState(null);
  const [editPartQuantity, setEditPartQuantity] = useState(0);
  const [editPartStockRoomId, setEditPartStockRoomId] = useState("");
  const [editPartStockRoomOptions, setEditPartStockRoomOptions] = useState([]);

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    setLoading(true);
    try {
      let getPartlistlink = masterAPI["partsReservation"].getByWorkOrderId;
      let response = await axiosPrivate.get(`${getPartlistlink}/${workOrderId}`);

      const mappedParts = [];
      if (Array.isArray(response.data.result)) {
        response.data.result.forEach(reservation => {
          if (Array.isArray(reservation.partsReservationItemsList)) {
            reservation.partsReservationItemsList.forEach(part => {
              mappedParts.push({
                id: part.id,
                partCode: part.partCode,
                partName: part.partName,
                quantity: part.quantity,
                stockRoomId: part.stockRoomId,
                stockRoomName: part.stockRoomName,
                partsId: part.partsId,
                partsReservationId: part.partsReservationId,
                partsReservationCode:reservation.code,
              });
            });
          }
        });
      }
      setPartList(mappedParts);
    } catch (error) {
      setPartList([]);
    }
    setLoading(false);
  };

  const confirmDeletePart = (id, partsReservationId) => {
    toast(
      <Grid>
        <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#797979" }}>Are you sure you want to delete?</Typography>
        <Grid item container spacing={2}>
          <Grid item>
            <Button
              onClick={() => {
                handlePartDelete(id, partsReservationId);
                toast.dismiss();
              }}
              sx={{ color: "grey", borderColor: "green" }}
              size="small"
              variant="outlined"
            >
              Yes
            </Button>
          </Grid>
          <Grid item>
            <Button
              onClick={() => toast.dismiss()}
              sx={{ color: "grey", borderColor: "red" }}
              size="small"
              variant="outlined"
            >
              No
            </Button>
          </Grid>
        </Grid>
      </Grid>,
      {
        closeButton: false,
        autoClose: false,
      }
    );
  };

  const handlePartDelete = async (id, partsReservationId) => {
    const partDelLink = masterAPI["partsReservation"].deleteParts;
    await axiosPrivate.delete(`${partDelLink}/${partsReservationId}/${id}`).then(res => {
      console.log(res);
      toast.success("Part deleted successfully");
      getData();
    }).catch(e => {
      console.log(e, "delete part error");
      toast.error("Error deleting part");
    });
  };

  const handlePartEdit = (item) => {
    setEditPartId(item.id);
    setEditPartQuantity(item.quantity);
    setEditPartStockRoomId(item.stockRoomId);
    const srLink = masterAPI["selectList"].stockRoomByPart;
    axiosPrivate.get(`${srLink}/${item.partsId}`)
      .then((res) => {
        setEditPartStockRoomOptions(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        setEditPartStockRoomOptions([]);
      });
  };

  const handlePartSave = async (item) => {
    const stockRoomName = editPartStockRoomOptions.find(s => s.value === editPartStockRoomId)?.text || item.stockRoomName;

    const partsReservationItemsList = [
      {
        id: item.id,
        partsReservationId: item.partsReservationId,
        quantity: Number(editPartQuantity),
        stockRoomId: editPartStockRoomId,
        partsId: item.partsId,
        stockRoomName: stockRoomName,
        partName: item.partName,
        partCode: item.partCode,
        partsReservationCode: item.partsReservationCode,
      }
    ];

    const payload = {
      id: item.partsReservationId,
      workOrderId: workOrderId,
      partsReservationItemsList: partsReservationItemsList,
    };

    try {
      await axiosPrivate.put(masterAPI["partsReservation"].update, payload);
      setPartList(partList.map(r => r.id === item.id ? { ...r, quantity: payload.partsReservationItemsList[0].quantity, stockRoomId: payload.partsReservationItemsList[0].stockRoomId, stockRoomName: stockRoomName,partsReservationCode:payload.partsReservationItemsList[0].partsReservationCode } : r));
      setEditPartId(null);
      toast.success("Part updated successfully");
    } catch (error) {
      toast.error("Error updating part");
    }
  };

  const handlePartCancel = () => {
    setEditPartId(null);
  };

  const partsColumns = [
   {
      Header: "Part Reservation Code",
      accessor: "partsReservationCode",
    },
    {
      Header: "Part Code",
      accessor: "partCode",
    },
    {
      Header: "Part Name",
      accessor: "partName",
    },
    {
      Header: "Stock Room",
      accessor: "stockRoomId",
      Cell: ({ row }) => (
        editPartId === row.original.id ? (
          <Autocomplete
            size="small"
            options={editPartStockRoomOptions}
            getOptionLabel={option => option.text || ""}
            value={editPartStockRoomOptions.find(s => s.value === editPartStockRoomId) || null}
            onChange={(e, value) => setEditPartStockRoomId(value ? value.value : "")}
            renderInput={params => <TextField {...params} placeholder="Stock Room" />}
            sx={{ width: 150 }}
          />
        ) : (
          row.original.stockRoomName || "-"
        )
      ),
    },
    {
      Header: "Quantity",
      accessor: "quantity",
      Cell: ({ row }) => (
        editPartId === row.original.id ? (
          <TextField
            type="number"
            size="small"
            value={editPartQuantity}
            onChange={e => setEditPartQuantity(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            autoFocus
            sx={{ width: 80 }}
          />
        ) : (
          row.original.quantity
        )
      ),
    },
    {
      Header: "Actions",
      Cell: ({ row }) => {
        const isEditing = editPartId === row.original.id;
        const editDisabled = !enableDisable?.edit ;
        const deleteDisabled = !enableDisable?.delete;

        return (
          <Box sx={{ whiteSpace: "nowrap" }}>
            {isEditing ? (
              <>
                <Button size="small" color="primary" onClick={() => handlePartSave(row.original)}>Save</Button>
                <Button size="small" color="inherit" onClick={handlePartCancel}>Cancel</Button>
              </>
            ) : (
              <>
                <IconButton disabled={editDisabled} onClick={() => handlePartEdit(row.original)}>
                  <EditOutlined
                    sx={{
                      fontSize: color.masterEditDelButton,
                      color: editDisabled ? "default" : color.masterEditDelButtonColor,
                    }}
                  />
                </IconButton>
                {/*<IconButton disabled={deleteDisabled} onClick={() => confirmDeletePart(row.original.id, row.original.partsReservationId)}>*/}
                {/*  <DeleteOutline sx={{ color: deleteDisabled ? "default" : "grey" }} />*/}
                {/*</IconButton>*/}
              </>
            )}
          </Box>
        );
      },
    },
  ];

  const handlePartClose = () => {
    setPartDialogue(false);
    getData();
  };

  return (
    <>
      {loading ? (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}><CircularProgress /></Box>
      ) : (
        <Grid container gap={"1rem"}>
          <Grid item xs={12} sx={{}}>
            {/* <Grid container alignItems={"center"} sx={{ mb: "0.5rem" }}>
              <Grid item xs={3}>
                <Typography sx={{ color: color.masterDialogTitleColor, fontSize: color.masterDialogFontSize }}>
                  Part
                </Typography>
              </Grid>
              <Grid container item xs={9} justifyContent={"flex-end"} gap={"1rem"}>
                <Tooltip title="Parts">
                  <IconButton onClick={() => setPartDialogue(true)}>
                    <Domain sx={{ fontSize: "1.7rem" }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
            <Divider sx={{ border: "1px solid rgb(0 0 0 / 17%)", mb: "15px" }} /> */}
            <NormalTable columns={partsColumns} data={partList} />
          </Grid>
        </Grid>
      )}
      {/* <Dialog
        open={partDialogue}
        onClose={handlePartClose}
        fullWidth
        maxWidth={"lg"}
      >
        <CreatePart handleCloseClick={handlePartClose} workOrderId={workOrderId} />
      </Dialog> */}
    </>
  );
};

export default Partlist;

